import { formatLocalizedNumber, isOneOf } from '@pba/utils';
import { Table as TableA } from 'antd';
import type { ColumnsType as ColumnsTypeA, TableProps } from 'antd/lib/table';
import type { SorterResult as SorterResultA } from 'antd/lib/table/interface';
import cn from 'classnames';
import type { MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { appLanguageCodes, locales } from '#i18n';
import type { Pagination } from '../../types';
import { Flex } from '../flex';
import { EmptyState } from './empty-state';
import { type SorterMap, useOnTableChange } from './hooks';
import { IconTitle } from './icon-title';
import styles from './table.module.css';

export type ColumnsType<T> = ColumnsTypeA<T>;
export type SorterResult<T> = SorterResultA<T>;

type P<T, TSorter extends SorterMap> = {
  clickableRows?: ((record: T) => boolean) | boolean;
  emptyState?: ReactNode;
  header?: {
    extra?: ReactNode;
    icon: ReactNode;
    title: string;
  };
  updateSearchParams?: (params: { pagination?: Partial<Pagination>; sorter?: TSorter }) => void;
} & TableProps<T>;

type TableClassNames = Exclude<TableProps['classNames'], (info: { props: TableProps }) => unknown>;

const mergeTableClassNames = (classNames?: TableClassNames) => ({
  ...classNames,
  body: {
    ...classNames?.body,
    cell: cn(styles['body-cell'], classNames?.body?.cell),
    row: cn(styles['body-row'], classNames?.body?.row),
  },
  content: cn(styles.content, classNames?.content),
  pagination: {
    ...classNames?.pagination,
    root: cn(styles.pagination, classNames?.pagination?.root),
  },
  root: cn(styles.table, classNames?.root),
  section: cn(styles.container, classNames?.section),
});

const TableComponent = <RecordType extends object, TSorter extends SorterMap = SorterMap>({
  className,
  classNames,
  clickableRows,
  dataSource,
  emptyState,
  header,
  loading,
  locale,
  onChange,
  onRow,
  rowClassName,
  rowSelection,
  updateSearchParams,
  ...rest
}: P<RecordType, TSorter>) => {
  const {
    i18n: { language },
    t,
  } = useTranslation(['translationReactComponents']);
  const numberFormatLocale = isOneOf(language, appLanguageCodes) ? locales[language] : locales.en;
  const onTableChange = useOnTableChange<RecordType, TSorter>({ updateSearchParams });
  const selectionColumnClassName = styles['selection-column'];
  const resolvedLoading = typeof loading === 'object' ? loading : { spinning: Boolean(loading) };
  const loadingClassNames = typeof resolvedLoading.classNames === 'object' ? resolvedLoading.classNames : undefined;

  return (
    <>
      {header ? (
        <Flex align="center" className={styles.header} justify="space-between">
          <IconTitle icon={header.icon} title={header.title} />
          {header.extra}
        </Flex>
      ) : null}
      <TableA
        bordered
        className={cn(className, { [styles.empty ?? '']: !dataSource?.length })}
        classNames={
          typeof classNames === 'function'
            ? (info) => mergeTableClassNames(classNames(info))
            : mergeTableClassNames(classNames)
        }
        dataSource={dataSource}
        loading={{
          ...resolvedLoading,
          classNames: {
            ...loadingClassNames,
            container: cn(styles['spin-container'], loadingClassNames?.container),
            root: cn(styles['spin-root'], loadingClassNames?.root),
          },
        }}
        locale={{
          ...locale,
          emptyText: emptyState,
        }}
        onChange={(tablePagination, tableFilter, tableSorter, extra) => {
          onTableChange(tablePagination, tableFilter, tableSorter, extra);
          onChange?.(tablePagination, tableFilter, tableSorter, extra);
        }}
        onRow={
          onRow
            ? (record, index) => {
                const rowProperties = onRow(record, index);

                return {
                  ...rowProperties,
                  onClick(event: MouseEvent<HTMLElement>) {
                    // Column with checkboxes is not clickable
                    if (
                      event.target instanceof HTMLElement &&
                      selectionColumnClassName &&
                      event.target.closest(`.${selectionColumnClassName}`)
                    ) {
                      return;
                    }

                    rowProperties.onClick?.(event);
                  },
                };
              }
            : undefined
        }
        rowClassName={(value, index, intent) =>
          cn(typeof rowClassName === 'string' ? rowClassName : rowClassName?.(value, index, intent), {
            [styles['row-clickable'] ?? '']: typeof clickableRows === 'function' ? clickableRows(value) : clickableRows,
          })
        }
        rowKey="id"
        {...rest}
        pagination={
          rest.pagination === false
            ? false
            : {
                placement: ['bottomStart'],
                showTotal: (total, range) =>
                  t('table.pagination', {
                    count: total,
                    from: formatLocalizedNumber({ locale: numberFormatLocale, value: range[0] }),
                    to: formatLocalizedNumber({ locale: numberFormatLocale, value: range[1] }),
                    total: formatLocalizedNumber({ locale: numberFormatLocale, value: total }),
                  }),
                ...rest.pagination,
              }
        }
        rowSelection={
          rowSelection
            ? {
                ...rowSelection,
                onCell(record, index) {
                  const cellProperties = rowSelection.onCell?.(record, index);

                  return {
                    ...cellProperties,
                    className: cn(selectionColumnClassName, cellProperties?.className),
                  };
                },
              }
            : undefined
        }
      />
    </>
  );
};

const Table = Object.assign(TableComponent, { Empty: EmptyState });

export default Table;

export { type FilterValue } from 'antd/lib/table/interface';
